import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api.js";
import { listGrooves, type Groove, type SharedGroove } from "./grooves.js";

/**
 * Somebody's library, searchable and pageable.
 *
 * The list stops at a hundred. Without a way past that, a tutor's hundred and
 * first groove was absent from their library *and* from the picker that
 * attaches one to a lesson note — reachable only by already knowing its
 * address, which is not a thing anybody knows. Search matters more than paging
 * here: a tutor who wants "the shuffle from last term" types it rather than
 * scrolling, and paging is the fallback for browsing.
 *
 * Shared between the library page and both pickers, so a groove found in one
 * is findable in the others.
 */
export interface GrooveLibrary {
  grooves: Groove[];
  shared: SharedGroove[];
  canCreate: boolean;
  loading: boolean;
  error: string | null;
  /** Null once there is nothing more to fetch. */
  hasMore: boolean;
  search: string;
  setSearch: (value: string) => void;
  loadMore: () => void;
  reload: () => void;
  /** Retry the failed page without discarding earlier pages. */
  retry: () => void;
}

export function useGrooveLibrary({ sort }: { sort?: "recent" | "title" } = {}): GrooveLibrary {
  const [search, setSearchRaw] = useState("");
  const [grooves, setGrooves] = useState<Groove[]>([]);
  const [shared, setShared] = useState<SharedGroove[]>([]);
  const [canCreate, setCanCreate] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Which request is the current one. A slow first page arriving after a
  // narrower search would otherwise put the wrong grooves on the screen.
  const latest = useRef(0);
  const failedPage = useRef<{ q: string; after: string | null; append: boolean } | null>(null);

  const fetchPage = useCallback(
    async (q: string, after: string | null, append: boolean) => {
      const ticket = (latest.current += 1);
      setLoading(true);
      setError(null);
      failedPage.current = null;
      try {
        const page = await listGrooves({
          ...(q === "" ? {} : { q }),
          ...(after === null ? {} : { cursor: after }),
          ...(sort === undefined ? {} : { sort }),
        });
        if (ticket !== latest.current) return;
        setGrooves((previous) => (append ? [...previous, ...page.grooves] : page.grooves));
        setShared(page.shared);
        setCanCreate(page.canCreate);
        setCursor(page.nextCursor);
      } catch (cause) {
        if (ticket !== latest.current) return;
        failedPage.current = { q, after, append };
        setError(cause instanceof ApiError ? cause.message : "Could not load your grooves.");
      } finally {
        if (ticket === latest.current) setLoading(false);
      }
    },
    [sort],
  );

  // Typing restarts the list rather than appending to it, so the cursor from
  // the previous search is dropped with it.
  useEffect(() => {
    const timer = setTimeout(() => void fetchPage(search.trim(), null, false), search === "" ? 0 : 250);
    return () => clearTimeout(timer);
  }, [search, nonce, fetchPage]);

  return {
    grooves,
    shared,
    canCreate,
    loading,
    error,
    hasMore: cursor !== null,
    search,
    setSearch: setSearchRaw,
    loadMore: () => {
      if (cursor !== null) void fetchPage(search.trim(), cursor, true);
    },
    reload: () => setNonce((n) => n + 1),
    retry: () => {
      const failed = failedPage.current;
      if (!loading && failed?.q === search.trim()) void fetchPage(failed.q, failed.after, failed.append);
    },
  };
}
